import { Order as OrderType, useDeleteOrder } from "@/hooks/useOrders";
import { useNavigate } from "react-router-dom";
import { useCart, CartItem } from "@/hooks/useCart";
import { useCoupon } from "@/hooks/useCoupons";
import { toast } from "react-toastify";
import Button from "@/components/ui/Button";
import { getApiInstance } from "@/utils/api";

const OrderCard = (props: {
  order: OrderType & { date?: string; time?: string };
  isMobile?: boolean;
}) => {
  const { order, isMobile = false } = props;
  const navigate = useNavigate();
  const { setCart, applyCoupon, setOrderData } = useCart();
  const deleteOrderMutation = useDeleteOrder();

  // جلب بيانات الكوبون إذا كان موجود في Order
  const { data: couponData } = useCoupon(order.coupon || "");

  // المبلغ قبل الخصم (احتياطي)
  const totalPrice = order.items.reduce(
    (sum, item) => sum + parseFloat(item.price),
    0
  );

  // المبلغ بعد الخصم
  const discountPct =
    typeof (couponData as any)?.discount_percentage === "number"
      ? (couponData as any).discount_percentage
      : 0;

  const itemsTotalAfterIfProvided = order.items.reduce((acc, item) => {
    const candidate = (item as any).total_after;
    if (candidate != null && candidate !== "") {
      const n = Number(candidate);
      if (!Number.isNaN(n)) return acc + n;
    }
    return acc + Number(item.price);
  }, 0);

  const hasPerItemTotalAfter = order.items.some(
    (it) => (it as any).total_after != null
  );

  const totalAfter = hasPerItemTotalAfter
    ? itemsTotalAfterIfProvided
    : Math.max(totalPrice - totalPrice * (discountPct / 100), 0);

  const handleDelete = async () => {
    try {
      await deleteOrderMutation.mutateAsync(order.id);
      toast.success("تم حذف الطلب بنجاح");
    } catch {
      toast.error("حدث خطأ في حذف الطلب");
    }
  };

  const handleAccept = async () => {
    const api = getApiInstance();

    try {
      console.log("📋 Order Data:", order);
      console.log("📦 Order Items:", order.items);

      const extractId = (val: any) => {
        if (val == null) return undefined;
        if (typeof val === "number" || typeof val === "string") return val;
        if (typeof val === "object" && "id" in val) return (val as any).id;
        return undefined;
      };

      const cartItems: CartItem[] = await Promise.all(
        order.items.map(async (item, index) => {
          console.log(`🔍 معالجة item ${index + 1}:`, item);

          let name = "عنصر";
          let icon: string | JSX.Element = "📦";
          let priceId: number | undefined;

          const serviceId = extractId(item.service);
          const subId = extractId(item.subscription);

          console.log(`🔍 Service ID: ${serviceId}, Subscription ID: ${subId}`);

          if (serviceId) {
            // جلب بيانات الـ service price المحدد
            try {
              console.log(`🔍 جلب بيانات service price ID: ${serviceId}`);
              const { data: priceData } = await api.get(
                `/store/service-prices/${serviceId}/`
              );
              console.log("✅ Service Price Data:", priceData);

              // جلب بيانات الخدمة نفسها للحصول على الاسم والصورة
              const { data: serviceData } = await api.get(
                `/store/services/${priceData.service}/`
              );
              console.log("✅ Service Data:", serviceData);

              name = `${serviceData.name} - ${getSizeLabel(priceData.size)}`;
              icon = serviceData.image || "🚗";
              priceId = priceData.id;
            } catch (error) {
              console.error("❌ خطأ في جلب service price:", error);
              console.error("❌ تفاصيل الخطأ:", (error as any).response?.data);

              // محاولة استخدام البيانات المتوفرة في الـ response إذا كانت موجودة
              if (item.service_info) {
                console.log(
                  "📋 استخدام service_info من الـ response:",
                  item.service_info
                );
                const serviceInfo = item.service_info;

                // البحث عن الـ price المحدد في قائمة الـ prices
                const targetPrice = serviceInfo.prices?.find(
                  (p: any) => p.id === serviceId
                );
                if (targetPrice) {
                  console.log("✅ وجد الـ price المحدد:", targetPrice);
                  name = `${serviceInfo.name} - ${getSizeLabel(
                    targetPrice.size
                  )}`;
                  icon = serviceInfo.image || "🚗";
                  priceId = targetPrice.id;
                } else {
                  console.log(
                    "❌ لم يتم العثور على الـ price المحدد في service_info"
                  );
                  console.log("🔍 البحث في prices:", serviceInfo.prices);
                  console.log("🔍 Service ID المطلوب:", serviceId);
                  name = serviceInfo.name;
                  icon = serviceInfo.image || "🚗";
                  priceId = serviceId;
                }
              } else {
                // fallback: جلب بيانات الخدمة مباشرة
                try {
                  const { data: serviceData } = await api.get(
                    `/store/services/${serviceId}/`
                  );
                  name = serviceData.name;
                  icon = serviceData.image || "🚗";
                  priceId = serviceId;
                } catch (fallbackError) {
                  console.error("❌ خطأ في fallback service:", fallbackError);
                  name = `خدمة ${serviceId}`;
                  icon = "🚗";
                  priceId = serviceId;
                }
              }
            }
          } else if (subId) {
            // جلب بيانات الـ subscription price المحدد
            try {
              console.log(`🔍 جلب بيانات subscription price ID: ${subId}`);
              const { data: priceData } = await api.get(
                `/store/subscription-prices/${subId}/`
              );
              console.log("✅ Subscription Price Data:", priceData);

              // جلب بيانات الاشتراك نفسه للحصول على الاسم والصورة
              const { data: subscriptionData } = await api.get(
                `/store/subscriptions/${priceData.subscription}/`
              );
              console.log("✅ Subscription Data:", subscriptionData);

              name = `${subscriptionData.name} - ${getSizeLabel(
                priceData.size
              )}`;
              icon = subscriptionData.image || "📅";
              priceId = priceData.id;
            } catch (error) {
              console.error("❌ خطأ في جلب subscription price:", error);
              console.error("❌ تفاصيل الخطأ:", (error as any).response?.data);

              // محاولة استخدام البيانات المتوفرة في الـ response إذا كانت موجودة
              if (item.subscription_info) {
                console.log(
                  "📋 استخدام subscription_info من الـ response:",
                  item.subscription_info
                );
                const subscriptionInfo = item.subscription_info;

                // البحث عن الـ price المحدد في قائمة الـ prices
                const targetPrice = subscriptionInfo.prices?.find(
                  (p: any) => p.id === subId
                );
                if (targetPrice) {
                  console.log("✅ وجد الـ price المحدد:", targetPrice);
                  name = `${subscriptionInfo.name} - ${getSizeLabel(
                    targetPrice.size
                  )}`;
                  icon = subscriptionInfo.image || "📅";
                  priceId = targetPrice.id;
                } else {
                  console.log(
                    "❌ لم يتم العثور على الـ price المحدد في subscription_info"
                  );
                  console.log("🔍 البحث في prices:", subscriptionInfo.prices);
                  console.log("🔍 Subscription ID المطلوب:", subId);
                  name = subscriptionInfo.name;
                  icon = subscriptionInfo.image || "📅";
                  priceId = subId;
                }
              } else {
                // fallback: جلب بيانات الاشتراك مباشرة
                try {
                  const { data: subscriptionData } = await api.get(
                    `/store/subscriptions/${subId}/`
                  );
                  name = subscriptionData.name;
                  icon = subscriptionData.image || "📅";
                  priceId = subId;
                } catch (fallbackError) {
                  console.error(
                    "❌ خطأ في fallback subscription:",
                    fallbackError
                  );
                  name = `اشتراك ${subId}`;
                  icon = "📅";
                  priceId = subId;
                }
              }
            }
          }

          const cartItem = {
            id: `order-${order.id}-item-${item.id}`,
            name,
            // نترك سعر العنصر كما هو، وسيتم تطبيق الخصم داخل السلة عبر الكوبون
            price: item.price.toString(),
            icon,
            quantity: 1,
            order_id: order.id,
            type: serviceId ? "service" : "subscription",
            price_id: priceId,
          } as CartItem;

          console.log("✅ Cart Item Created:", cartItem);
          return cartItem;
        })
      );

      const orderData = {
        order_id: order.id,
        phone: order.user_info?.phone || "",
        name: order.user_info?.name || "",
        coupon: order.coupon,
      };

      // تطبيق البيانات على Cart
      setCart(cartItems);
      setOrderData(orderData);

      // إضافة الكوبون إذا وجد مع النسبة الصحيحة
      if (order.coupon && couponData) {
        applyCoupon({
          code: order.coupon,
          discount_percentage: couponData.discount_percentage,
          valid_from: couponData.valid_from,
          valid_to: couponData.valid_to,
          is_active: couponData.is_active,
          num_of_uses: couponData.num_of_uses,
        });
      }

      toast.success("تم نقل الطلب إلى السلة بنجاح");
      navigate("/cart");
    } catch (error) {
      toast.error("حدث خطأ أثناء جلب بيانات العناصر");
    }
  };

  // دالة مساعدة لتحويل حجم السيارة إلى نص مقروء
  const getSizeLabel = (size: string): string => {
    const sizeLabels: Record<string, string> = {
      motorcycle: "دراجة نارية",
      small: "سيارة S",
      medium: "سيارة M",
      large: "سيارة L",
      extra_large: "سيارة XL",
    };
    return sizeLabels[size] || size;
  };

  return (
    <div
      className={`border border-gray-300 hover:border-red-300 rounded-2xl ${
        isMobile ? "p-3" : "p-5"
      } bg-white flex justify-between items-start gap-6 text-right shadow-sm hover:bg-red-50 transition`}
    >
      {/* معلومات الطلب - على اليمين */}
      <div
        className={`flex-1 ${
          isMobile ? "text-base space-y-1" : "text-lg space-y-2"
        }`}
      >
        {/* رقم الطلب */}
        <div
          className={`font-bold ${
            isMobile ? "text-xl" : "text-2xl"
          } text-black`}
        >
          طلب {order.id}
        </div>

        {/* اسم العميل */}
        <div className={`${isMobile ? "text-base" : "text-lg"} text-gray-700`}>
          {order.user_info?.name || "اسم العميل"}
        </div>

        {/* رقم الجوال */}
        <div className={`${isMobile ? "text-base" : "text-lg"} text-gray-700`}>
          {order.user_info?.phone || "رقم الجوال"}
        </div>

        {/* عدد الاصناف */}
        <div className={`${isMobile ? "text-base" : "text-lg"} text-gray-700`}>
          عدد الاصناف {order.items.length}
        </div>

        {/* السعر الكلي */}
        <div
          className={`text-red-600 font-bold ${
            isMobile ? "text-lg" : "text-xl"
          } mt-2`}
        >
          السعر النهائي: {totalAfter.toFixed(2)} ريال
        </div>
      </div>

      {/* الوقت والتاريخ والأزرار - على الشمال */}
      <div className={`flex flex-col gap-4 min-w-[160px] items-center`}>
        {/* الوقت */}
        <div className={`text-gray-600 ${isMobile ? "text-sm" : "text-base"}`}>
          وقت الحجز: {order.time || "غير محدد"}
        </div>

        {/* التاريخ */}
        <div
          className={`text-red-600 font-medium ${
            isMobile ? "text-sm" : "text-base"
          }`}
        >
          يوم الحجز:{" "}
          {order.date
            ? new Date(order.date).toLocaleDateString("ar-EG")
            : "غير محدد"}
        </div>

        {/* الأزرار جنب بعض */}
        <div className='flex gap-2 w-full'>
          <Button
            onClick={handleAccept}
            confirm
            confirmTitle={`تأكيد القبول - الإجمالي ${totalAfter.toFixed(
              2
            )} ريال`}
            confirmMessage={`سيتم نقل الطلب للسلة بالسعر النهائي ${totalAfter.toFixed(
              2
            )} ريال. هل أنت متأكد؟`}
            confirmText='نعم'
            cancelText='لا'
            type='primary'
            className='flex-1 py-1.5 text-xs'
          >
            تأكيد ({totalAfter.toFixed(2)} ر.س)
          </Button>

          <Button
            onClick={handleDelete}
            confirm
            confirmTitle='تأكيد الحذف'
            confirmMessage='هل متأكد من حذف هذا الطلب؟'
            confirmText='نعم'
            cancelText='لا'
            type='secondary'
            disabled={deleteOrderMutation.isPending}
            className='flex-1 py-1.5 text-xs'
          >
            حذف
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OrderCard;
